import { useEffect, useMemo, useState } from "react";
import {
  getPermisos,
  getPermisosByRol,
  assignPermisosToRol,
} from "../../../services/usuarios_tienda/permisos";
import "./AsignarPermisosModal.css";

function AsignarPermisosModal({ rolTienda, rol, onClose }) {
  const [permisos, setPermisos] = useState([]);
  const [checked, setChecked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");

  const roleData = rolTienda || rol;
  const rolId = roleData?.rol_tienda_id;

  const getNiceErrorMessage = (error, fallback = "La operación falló.") => {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 220 ? `${clean.slice(0, 220)}…` : clean;
  };

  const showAlert = (type, message, ms = 3500) => {
    setAlert({ type, message });
    window.clearTimeout(showAlert._t);
    showAlert._t = window.setTimeout(() => setAlert(null), ms);
  };

  useEffect(() => {
    if (!rolId) return;

    const loadData = async () => {
      setLoading(true);
      setAlert(null);

      try {
        const [allPermisosResult, permisosRolResult] = await Promise.allSettled([
          getPermisos(),
          getPermisosByRol(rolId),
        ]);

        const allPermisosResp =
          allPermisosResult.status === "fulfilled" ? allPermisosResult.value : null;
        const permisosRolResp =
          permisosRolResult.status === "fulfilled" ? permisosRolResult.value : null;

        const permisosCatalogo = Array.isArray(allPermisosResp)
          ? allPermisosResp
          : Array.isArray(allPermisosResp?.permisos)
          ? allPermisosResp.permisos
          : [];

        const permisosAsignados = Array.isArray(permisosRolResp)
          ? permisosRolResp
          : Array.isArray(permisosRolResp?.permisos)
          ? permisosRolResp.permisos
          : [];

        setPermisos(permisosCatalogo);
        setChecked(permisosAsignados.map((id) => Number(id)));

        if (allPermisosResult.status === "rejected") {
          throw allPermisosResult.reason;
        }

        if (permisosRolResult.status === "rejected") {
          console.error("No se pudieron cargar los permisos asignados del rol:", permisosRolResult.reason);
          showAlert(
            "info",
            "Se cargó el catálogo de permisos, pero no se pudieron leer los permisos ya asignados a este rol."
          );
        }
      } catch (err) {
        console.error("Error cargando permisos para el rol:", err);
        setPermisos([]);
        setChecked([]);
        showAlert("error", getNiceErrorMessage(err, "No se pudieron cargar los permisos."));
      } finally {
        setLoading(false);
      }
    };

    loadData();

    return () => {
      window.clearTimeout(showAlert._t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolId]);

  const permisosFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return permisos;

    return permisos.filter((permiso) => {
      const clave = String(permiso?.clave || "").toLowerCase();
      const descripcion = String(permiso?.descripcion || "").toLowerCase();
      const modulo = String(permiso?.modulo || "").toLowerCase();

      return (
        clave.includes(q) ||
        descripcion.includes(q) ||
        modulo.includes(q)
      );
    });
  }, [permisos, search]);

  const toggleCheck = (permisoTiendaId) => {
    setChecked((prev) =>
      prev.includes(permisoTiendaId)
        ? prev.filter((id) => id !== permisoTiendaId)
        : [...prev, permisoTiendaId]
    );
  };

  const marcarTodosVisibles = () => {
    const visiblesIds = permisosFiltrados.map((p) => Number(p.permiso_tienda_id));

    setChecked((prev) => {
      const set = new Set(prev);
      visiblesIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  const desmarcarTodosVisibles = () => {
    const visiblesIds = new Set(
      permisosFiltrados.map((p) => Number(p.permiso_tienda_id))
    );

    setChecked((prev) => prev.filter((id) => !visiblesIds.has(id)));
  };

  const save = async () => {
    if (!rolId) return;

    setSaving(true);
    setAlert(null);

    try {
      await assignPermisosToRol(rolId, checked);
      showAlert("success", "Permisos actualizados correctamente.");
      window.setTimeout(() => onClose?.(), 600);
    } catch (err) {
      console.error("Error actualizando permisos:", err);
      showAlert("error", getNiceErrorMessage(err, "Error actualizando permisos."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="tienda-modal__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="perm-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="perm-modal__header">
          <div className="perm-modal__header-text">
            <p className="perm-modal__eyebrow">Configuración de permisos</p>
            <h2 className="perm-modal__title">{roleData?.nombre || "Rol sin nombre"}</h2>
            <p className="perm-modal__subtitle">
              Rol #{roleData?.rol_tienda_id} · Selecciona los permisos que tendrá este rol.
            </p>
          </div>

          <button type="button" onClick={onClose} className="perm-modal__close" title="Cerrar">
            ✕
          </button>
        </div>

        {alert && (
          <div
            className={`tienda-modal__alert tienda-modal__alert--${
              alert.type === "error"
                ? "error"
                : alert.type === "success"
                ? "success"
                : "info"
            }`}
            style={{ margin: "0 0 14px" }}
          >
            <span>{alert.message}</span>
            <button type="button" className="tienda-modal__alert-x" onClick={() => setAlert(null)}>
              ✕
            </button>
          </div>
        )}

        <div className="perm-modal__toolbar">
          <input
            type="text"
            placeholder="Buscar por clave, descripción o módulo"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="perm-modal__search"
            disabled={loading || saving}
          />

          <div className="perm-modal__toolbar-actions">
            <button
              type="button"
              className="perm-modal__ghost-btn"
              onClick={marcarTodosVisibles}
              disabled={loading || saving || permisosFiltrados.length === 0}
            >
              Marcar visibles
            </button>

            <button
              type="button"
              className="perm-modal__ghost-btn"
              onClick={desmarcarTodosVisibles}
              disabled={loading || saving || permisosFiltrados.length === 0}
            >
              Desmarcar visibles
            </button>
          </div>
        </div>

        <div className="perm-modal__summary">
          <span className="perm-modal__pill">
            {checked.length} permiso{checked.length === 1 ? "" : "s"} seleccionado
            {checked.length === 1 ? "" : "s"}
          </span>

          <span className="perm-modal__muted">
            {permisosFiltrados.length} mostrado{permisosFiltrados.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className={`perm-modal__list ${loading ? "is-loading" : ""}`}>
          {loading ? (
            <div className="perm-modal__empty">Cargando permisos…</div>
          ) : permisos.length === 0 ? (
            <div className="perm-modal__empty">No se encontraron permisos</div>
          ) : permisosFiltrados.length === 0 ? (
            <div className="perm-modal__empty">No hay coincidencias para tu búsqueda</div>
          ) : (
            permisosFiltrados.map((permiso) => {
              const permisoId = Number(permiso.permiso_tienda_id);
              const isChecked = checked.includes(permisoId);

              return (
                <label key={permisoId} className="perm-modal__item">
                  <div className="perm-modal__item-check">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleCheck(permisoId)}
                      disabled={saving}
                    />
                  </div>

                  <div className="perm-modal__item-body">
                    <div className="perm-modal__item-top">
                      <span className="perm-modal__item-title">
                        {permiso.descripcion || permiso.clave || `Permiso ${permisoId}`}
                      </span>

                      <span
                        className={`perm-modal__badge ${
                          Number(permiso.estado) === 1
                            ? "perm-modal__badge--active"
                            : "perm-modal__badge--inactive"
                        }`}
                      >
                        {Number(permiso.estado) === 1 ? "Activo" : "Inactivo"}
                      </span>
                    </div>

                    <div className="perm-modal__meta">
                      <span className="perm-modal__tag">#{permisoId}</span>
                      {permiso.clave ? <span className="perm-modal__tag">{permiso.clave}</span> : null}
                      {permiso.modulo ? <span className="perm-modal__tag perm-modal__tag--soft">{permiso.modulo}</span> : null}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <div className="perm-modal__footer">
          <button
            type="button"
            onClick={save}
            className="perm-modal__btn perm-modal__btn--primary"
            disabled={saving || loading}
          >
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>

          <button type="button" onClick={onClose} className="perm-modal__btn perm-modal__btn--secondary" disabled={saving}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default AsignarPermisosModal;